/** @graphql agg **/
import { useCallback, useEffect, useState } from 'react';
import { fetchQuery, graphql, useFragment, type GraphQLTaggedNode } from 'react-relay';

import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useExternalLabelsQuery,
	useExternalLabelsQuery$data,
} from './__generated__/useExternalLabelsQuery.graphql.ts';
import type {
	useExternalLabels_query$data,
	useExternalLabels_query$key,
} from './__generated__/useExternalLabels_query.graphql.ts';

/**
 * Standalone Relay read of the managed (external) labels applied to content,
 * gated behind `confluence_managed_external_labels_endpoint`.
 *
 * View Page surfaces (`FooterLabels`, details-panel `LabelsSection`) instead read
 * via the colocated `useExternalLabels_query` fragment (see
 * `useExternalLabelsFragment`). This standalone hook remains for the legacy label
 * picker (`useLabelsSelectProps`), whose `inheritedLabelsData` path is a
 * `Content`-node fragment that can't carry the root `Query` field; migrating it to
 * fragment ownership is a follow-up. Uses `fetchQuery` (not a suspending
 * `useLazyLoadQuery`) to keep the imperative `{ labels, loading, error, refetch }`
 * contract and self-skip when the gate is off.
 */
export const EXTERNAL_LABELS_QUERY: GraphQLTaggedNode = graphql`
	query useExternalLabelsQuery($cloudId: ID!, $id: ID!) {
		confluence_externalLabels(cloudId: $cloudId, id: $id) {
			id
			kind
			name
		}
	}
`;

/**
 * Colocated fragment on `Query` for the managed labels field, spread into the View
 * Page `ContentUnifiedQuery` (warmed into the store by route preload) and each
 * surface's own query, then read via `useFragment`. The spreading operation gates
 * it via `@include(if: $includeExternalLabels)`; when excluded the ref is absent
 * and the read returns an empty list.
 */
export const EXTERNAL_LABELS_FRAGMENT: GraphQLTaggedNode = graphql`
	fragment useExternalLabels_query on Query
	@argumentDefinitions(cloudId: { type: "ID!" }, id: { type: "ID!" }) {
		confluence_externalLabels(cloudId: $cloudId, id: $id) {
			id
			kind
			name
		}
	}
`;

export type ExternalLabel = NonNullable<
	NonNullable<useExternalLabelsQuery$data['confluence_externalLabels']>[number]
>;

export type UseExternalLabelsResult = {
	labels: readonly ExternalLabel[];
	loading: boolean;
	error: Error | undefined;
	refetch: () => void;
};

// Single state object so each transition is one atomic update.
type ExternalLabelsState = {
	labels: readonly ExternalLabel[];
	loading: boolean;
	error: Error | undefined;
};

const SETTLED_EMPTY: ExternalLabelsState = { labels: [], loading: false, error: undefined };

// Builds the page ARI identifying the content. Exported so the route preloader and
// each consumer query build the same `confluence_externalLabels` args (cloudId + ARI).
export const buildExternalLabelsAri = (cloudId: string, contentId: string): string =>
	cloudId && contentId ? `ari:cloud:confluence:${cloudId}:page/${contentId}` : '';

export const useExternalLabels = (contentId: string): UseExternalLabelsResult => {
	const { cloudId } = useSessionData();
	const entityAri = buildExternalLabelsAri(cloudId, contentId);

	const skip = !fg('confluence_managed_external_labels_endpoint') || !cloudId || !contentId;

	// Start loading when active so consumers don't flash empty before the first read.
	const [state, setState] = useState<ExternalLabelsState>(() =>
		skip ? SETTLED_EMPTY : { labels: [], loading: true, error: undefined },
	);

	const runQuery = useCallback(() => {
		if (skip) {
			setState(SETTLED_EMPTY);
			return () => {};
		}

		setState((prev) => ({ ...prev, loading: true, error: undefined }));

		let cancelled = false;

		const subscription = fetchQuery<useExternalLabelsQuery>(
			getRelayEnvironment(),
			EXTERNAL_LABELS_QUERY,
			{ cloudId, id: entityAri },
			{
				// Read store-first (warmed by `preloadManagedLabels` during SSR),
				// network otherwise. No `X-Query-Context` header: the field resolves
				// headerless, which is what lets it fold into the batched
				// ContentUnifiedQuery.
				fetchPolicy: 'store-or-network',
			},
		).subscribe({
			next: (data) => {
				if (cancelled) {
					return;
				}
				// Gateway types array and elements as nullable; drop nulls.
				setState({
					labels: (data?.confluence_externalLabels ?? []).filter(
						(label): label is ExternalLabel => label != null,
					),
					loading: false,
					error: undefined,
				});
			},
			error: (err: unknown) => {
				if (cancelled) {
					return;
				}
				setState((prev) => ({
					...prev,
					loading: false,
					error: err instanceof Error ? err : new Error(String(err)),
				}));
			},
		});

		return () => {
			cancelled = true;
			subscription.unsubscribe();
		};
	}, [skip, cloudId, entityAri]);

	// `runQuery` returns its cleanup, tearing down the in-flight fetch on unmount/re-run.
	useEffect(() => {
		return runQuery();
	}, [runQuery]);

	return {
		labels: state.labels,
		loading: state.loading,
		error: state.error,
		refetch: () => {
			if (!skip) {
				runQuery();
			}
		},
	};
};

/**
 * Reads managed (external) labels from a `useExternalLabels_query` fragment ref
 * exposed by a parent operation. Pure store read — loading/error belong to the
 * parent query. The gate lives on the spreading operation, so an absent ref yields
 * an empty list; callers still gate the managed-label UI inline.
 */
export const useExternalLabelsFragment = (
	fragmentRef: useExternalLabels_query$key | null | undefined,
): readonly ExternalLabel[] => {
	// Native `useFragment` calls `useRelayEnvironment()` even for a null ref, so it
	// throws when no provider is mounted (Apollo mode / Apollo-mode jest tests). The
	// spread only exists when the gate is on, so skip the read entirely when it's off.
	// The gate is session-stable within a render tree, matching `useReadFragmentInterop`.
	const data: useExternalLabels_query$data | null | undefined = fg(
		'confluence_managed_external_labels_endpoint',
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- gate is session-stable within a render tree, matching useReadFragmentInterop
			useFragment(EXTERNAL_LABELS_FRAGMENT, fragmentRef ?? null)
		: null;

	// Gateway types array and elements as nullable; drop nulls.
	return (data?.confluence_externalLabels ?? []).filter(
		(label): label is ExternalLabel => label != null,
	);
};

export type ExternalLabelsFragmentVariables = {
	includeExternalLabels: boolean;
	cloudId: string;
	id: string;
};

/**
 * Builds the three `useExternalLabels_query` spread variables for a consumer query,
 * deriving `includeExternalLabels` everywhere from the managed-labels gate.
 * `cloudId`/`id` are empty strings when excluded, in which case the `@include`-gated
 * spread is skipped and the server never reads them.
 */
export const useExternalLabelsFragmentVariables = (
	contentId: string,
): ExternalLabelsFragmentVariables => {
	const { cloudId } = useSessionData();
	const includeExternalLabels =
		Boolean(cloudId) && Boolean(contentId) && fg('confluence_managed_external_labels_endpoint');

	return {
		includeExternalLabels,
		cloudId: includeExternalLabels ? cloudId : '',
		id: includeExternalLabels ? buildExternalLabelsAri(cloudId, contentId) : '',
	};
};
